/**
 * Store/feature composition for the split-view canvas: pan, zoom and
 * rotation, plus the page-selection slice. No
 * translations.
 */
import { useMemo } from 'react';
import * as R from 'remeda';
import { useShallow } from 'zustand/react/shallow';
import {
  CanvasProvider,
  composeStore,
  createPanFeature,
  createStoreHooks,
  createZoomFeature,
  getHandlers,
  type InferCanvasStore,
  withCameraSlice,
} from '../canvas';
import {
  composeGetEffectiveDimensions,
  createRotationFeature,
  DocumentCanvasContextProvider,
  extractTransformContributions,
  getTransformWrappers,
  useInitialFit,
  withDocumentPagesSlice,
} from '../document-canvas';
import { withPageSelectionSlice } from './features/page-selection';

const createFeatures = () =>
  [createPanFeature(), createZoomFeature(), createRotationFeature()] as const;

type Features = ReturnType<typeof createFeatures>;

type FeatureSlice = NonNullable<Features[number]['slice']>;

const createSplitCanvasStore = () => {
  const features = createFeatures();

  const featureSlices = features
    .map(f => f.slice)
    .filter((s): s is FeatureSlice => s !== undefined);

  const slices = R.pipe(
    featureSlices,
    withDocumentPagesSlice,
    withCameraSlice,
    withPageSelectionSlice
  );

  const { store } = composeStore(...slices);

  const contributions = extractTransformContributions(features, store.getState);
  const getEffectiveDimensions = composeGetEffectiveDimensions(features);
  const transformWrappers = getTransformWrappers(contributions);

  return {
    store,
    handlers: getHandlers(features),
    getEffectiveDimensions,
    transformWrappers,
  };
};

export type SplitCanvasStore = InferCanvasStore<typeof createSplitCanvasStore>;

const { useStore, useStoreApi } = createStoreHooks<SplitCanvasStore>();

type SplitCanvasProviderProps = {
  children: React.ReactNode;
};

export const SplitCanvasProvider = ({ children }: SplitCanvasProviderProps) => {
  const { store, handlers, getEffectiveDimensions, transformWrappers } =
    useMemo(() => createSplitCanvasStore(), []);

  useInitialFit(store);

  const contextValue = useMemo(
    () => ({ getEffectiveDimensions, transformWrappers }),
    [getEffectiveDimensions, transformWrappers]
  );

  return (
    <CanvasProvider store={store} handlers={handlers}>
      <DocumentCanvasContextProvider value={contextValue}>
        {children}
      </DocumentCanvasContextProvider>
    </CanvasProvider>
  );
};

export const useSplitCanvasStore = useStore;
/** @public */
export const useSplitCanvasStoreApi = useStoreApi;

/** @public */
export const useSplitCanvasActions = () =>
  useSplitCanvasStore(
    useShallow(state => ({
      ...state.canvas.actions,
      ...state.zoom.actions,
      ...state.pan.actions,
    }))
  );
